/**
 * Tag Excel Title Mappings
 *
 * This file defines the mappings from Excel column headers to property names
 * for all tag types in the application. These mappings are the single source
 * of truth for property definitions used across features like TagHarvester
 * and Masters Import.
 *
 * Format: { 'Excel Column Header': 'propertyName' }
 */

export const equipmentExcelTitlesMap = {
    'Tag No.*': 'name',
    'Plant': 'plant',
    'Unit': 'unit',
    'Functional Location': 'funcLocation',
    'Area': 'area',
    'System': 'system',
    'Sub System': 'subSystem',
    'Insulation Code': 'insulationCode',
    'Document No.': 'documentNo',
    'Eq - Tier 1': 'eqTier1',
    'Eq - Tier 2': 'eqTier2',
    'Eq - Tier 3': 'eqTier3',
    'Pipeline Specification': 'pipeSpecification',
    'Equipment Class': 'equipmentClass',
    'Parent Tag': 'parentTag',
    'Service Description': 'serviceDescription',
    'Manufacturer': 'manufacturer',
    'Model No.': 'modelNo',
    'Serial No.': 'serialNo',
    'Process Function': 'processFunction',
    'Equipment Name / Description': 'equipmentDescription',
    'Pipeline': 'lineNo',
    'Cable No.': 'cableNo',
    'PO No.': 'poNo',
    'Fire Zone': 'fireZone',
    'Discipline': 'discipline',
    'Location': 'location',
    'Certifying Authority': 'certifyingAuthority',
    'Certificate No': 'certificateNo',
    'Weight': 'weight',
    'Type / Category': 'type',
    'Operational Status': 'operationalStatus',
    'Date of Installation': 'dateOfInstallation',
    'Technical Specification': 'technicalSpecification',
    'Is PRD': 'isPrd',
    'PRD Overhaul Date': 'prdOverhaulDate',
    'Maintenance Schedule': 'maintenanceSchedule',
    'Warranty Information': 'warrantyInformation',
    'Criticality Rating': 'criticalityRating',
    'Spare Parts (Yes/No)': 'spareParts',
    'P&ID No.': 'p_Id'
};

export const pipelineExcelTitlesMap = {
    'Tag No.*': 'name',
    'Plant': 'plant',
    'Unit': 'unit',
    'Functional Location': 'funcLocation',
    'Area': 'area',
    'System': 'system',
    'Sub System': 'subSystem',
    'Insulation Code': 'insulationCode',
    'Document No.': 'documentNo',
    'Line - Tier 1': 'lineTier1',
    'Line - Tier 2': 'lineTier2',
    'Line - Tier 3': 'lineTier3',
    'Line Size (inches)': 'lineSize',
    'Line Routing': 'lineRouting',
    'Pipe Specification': 'pipeSpecification',
    'Sequence': 'sequenceNumber',
    'NPD (Nominal Pipe Diameter)': 'npd',
    'From': 'from',
    'To': 'to',
    'P&ID From/To': 'p_IdFromTo',
    'Fluid Code': 'fluidCode',
    'Fluid Description': 'fluidDescription',
    'COVE': 'cove',
    'Insulation Thickness (mm)': 'insulationMm',
    'Tracing - Max Temperature (°C)': 'tracingMaxTemperature',
    'Tracing - Maintenance Temperature (°C)': 'tracingMaintenanceTemperature',
    'Phase': 'phase',
    'Sour Service': 'sourService',
    'Operating - Pressure (barg)': 'operatingPressure',
    'Operating - Temperature (°C)': 'operatingTemperature',
    'Pmax @ Tmin - Pressure (barg)': 'pmaxAtTminPressure',
    'Pmax @ Tmin - Temperature (°C)': 'pmaxAtTminTemperature',
    'Pmax @ Tmax - Pressure (barg)': 'pmaxAtTmaxPressure',
    'Pmax @ Tmax - Temperature (°C)': 'pmaxAtTmaxTemperature',
    'Chemical Cleaning': 'chemicalCleaning',
    'Process Critical': 'processCritical',
    'Test Conditions - Pressure (barg)': 'testConditionsPressure',
    'Test Conditions - Medium': 'testConditionsMedium',
    'MDMT (°C)': 'mdmt',
    'Test Type - Pressure (barg)': 'testTypePressure',
    'Design - Maximum Pressure (barg)': 'designMaxPressure',
    'Design - Minimum Pressure (barg)': 'designMinPressure',
    'Design - Maximum Temperature (°C)': 'designMaxTemperature',
    'Design - Minimum Temperature (°C)': 'designMinTemperature',
    'PED - Fluid State': 'pedFluidState',
    'PED - Fluid Group': 'pedFluidGroup',
    'PED - Fluid Category': 'pedCategory',
    'Criticality': 'stressCriticality',
    'Stress Calculation Number': 'stressCalculationNumber',
    'Mass Flow Rated (kg/h)': 'massFlow',
    'Liq:Am3/h': 'volFlowLiq',
    'Vap:NM3/h': 'volFlowVap',
    'Weight % Vapor': 'weightVapor',
    'Pressure (barg)': 'pressure',
    'Insulation Temperature (°C)': 'tempInsulTemp',
    'Total Viscosity (cP)': 'totalViscosity',
    'Total Density (Kg/m3)': 'totalDensity',
    'Velocity Rated (m/s)': 'velocity',
    'Δ P Rated (bar/Km)': 'deltaP',
    'Lethal Services': 'lethalServices',
    'Toxic Service': 'toxicService',
    'Eval Weld Imperfection - Fluid Category': 'evalWeldImperfectionFluidCategory'
};

export const structuralExcelTitlesMap = {
    'Tag No.*': 'name',
    'Plant': 'plant',
    'Unit': 'unit',
    'Functional Location': 'funcLocation',
    'Area': 'area',
    'System': 'system',
    'Sub System': 'subSystem',
    'Insulation Code': 'insulationCode',
    'Document No.': 'documentNo',
    'Type': 'type',
    'Description': 'description',
    'Spec': 'spec',
    'Weight': 'weight',
    'Section ID': 'sectionId',
    'Section Description': 'sectionDescription',
    'Material Type': 'materialType',
    'Section Dimensions': 'sectionDimensions',
    'Structural Role': 'structuralRole',
    'Critical Rating': 'criticalRating'
};

export const inspectionExcelTitlesMap = {
    'Tag No.*': 'name',
    'Plant': 'plant',
    'Unit': 'unit',
    'Functional Location': 'funcLocation',
    'Area': 'area',
    'System': 'system',
    'Sub System': 'subSystem',
    'Insulation Code': 'insulationCode',
    'Document No.': 'documentNo',
    'ID': 'friendlyId',
    'Asset Class': 'assetClass',
    'Tag Description (Asset ID)': 'tagDescription',
    'Unit Code': 'pltCode',
    'Item Type': 'macType',
    'Equipment / Pipeline No.': 'lno',
    'Line No. Code': 'lnoCode',
    'Category': 'ctgName',
    'Work Order No.': 'workOrderNo',
    'CL No.': 'clName',
    'From': 'macFrom',
    'To': 'macTo',
    'General Regulations': 'lawName',
    'Manufacturer': 'mkrName',
    'Date of Production': 'makDate',
    'Criticality': 'criticality',
    'Type': 'type',
    'Function': 'fncName',
    'Flow Class': 'flwClsName',
    'Flow Name': 'flwName',
    'Status': 'useCase',
    'Inspection Date': 'ispDate',
    'Corrosion Rate': 'cr',
    'Remaining Life': 'rl',
    'End of Life': 'el',
    'Min Thickness': 'minThickness',
    'Measurement Thickness': 'measuredThickness',
    'Remaining Thickness': 'rt',
    'Next Inspection Date': 'nIspDate',
    'Next Visual/NDI Inspection': 'offerNextInspDate',
    'Latest VI/NDI Inspected Year': 'offerLastInspDate',
    'Failure Type': 'failureType',
    'Failure Date': 'failureDate',
    'Current Risk - September 2024': 'currentRisk',
    'Future Risk w/o Inspections - September 2034': 'futureRisk',
    'Future Risk w/ Inspections - November 2034': 'futureRiskInspections',
    'Operating Pressure 1': 'pressure1',
    'Operating Pressure 2': 'pressure2',
    'Operating Pressure 3': 'pressure3',
    'Operating Pressure 4': 'pressure4',
    'Pressure Unit Type': 'pressureUnit',
    'Operating Temperature 1': 'temperature1',
    'Operating Temperature 2': 'temperature2',
    'Operating Temperature 3': 'temperature3',
    'Operating Temperature 4': 'temperature4',
    'Temperature Unit Type': 'temperatureUnit',
    'Corrosion Loop No. 1': 'clNo1',
    'Corrosion Loop No. 2': 'clNo2',
    'Fluid Classification 1': 'fluidClassification1',
    'Fluid Classification 2': 'fluidClassification2',
    'Code 1': 'lawName1',
    'Code 2': 'lawName2',
    'Code 3': 'lawName3',
    'Inspection Report Number': 'reportNumber',
    'Inspection Report Created Date': 'createdDate',
    'Inspection Report Issued Date': 'issuedDate',
    'Report Audited': 'isReportAudited',
    'Inspection Recommendation Date': 'recommendationDate',
    'Inspection Results Approval Status': 'resultsApprovalStatus',
    'Inspection Results Pending Report': 'resultsPendingReport',
    'Inspection Results Excess Corrosion': 'resultsExcessCorrosion',
    'FRBI Analysis Created Date': 'frbiAnalysisCreatedDate',
    'Target Degradation Factor (DF) Date': 'targetDfDate',
    'Recommendation': 'recommendation',
    'Risk Matrix Rating': 'riskMatrixRating',
    'IOW Excursion of Limits': 'iowExcursionOfLimits',
    'Cathodic Protection Start Date': 'cathodicProtectionStartDate',
    'Cathodic Protection End Date': 'cathodicProtectionEndDate',
    'Cathodic Protection Status': 'cathodicProtectionStatus',
    'Protected/Un-Protected': 'protectedUnprotected',
    'Equipment Start Date': 'equipmentStartDate',
    'Equipment End Date': 'equipmentEndDate',
    'Inspection Iso Number': 'inspectionIsoNumber',
    'Inspection Iso Revision Number': 'inspectionIsoRevisionNumber',
    'P&ID Drawing Number': 'p_IdDrawingNumber',
    'P&ID Revision Number': 'p_IdRevisionNumber'
};

export const anomalyExcelTitlesMap = {
    'Tag No.*': 'name',
    'Plant': 'plant',
    'Unit': 'unit',
    'Functional Location': 'funcLocation',
    'Area': 'area',
    'System': 'system',
    'Sub System': 'subSystem',
    'Insulation Code': 'insulationCode',
    'Document No.': 'documentNo'
};

export const annotationExcelTitlesMap = {
    'Tag No.*': 'name',
    'Plant': 'plant',
    'Unit': 'unit',
    'Functional Location': 'funcLocation',
    'Area': 'area',
    'System': 'system',
    'Sub System': 'subSystem',
    'Insulation Code': 'insulationCode',
    'Document No.': 'documentNo'
};

export const scaffoldExcelTitlesMap = {
    'Tag No.*': 'name',
    'Plant': 'plant',
    'Unit': 'unit',
    'Functional Location': 'funcLocation',
    'Area': 'area',
    'System': 'system',
    'Sub System': 'subSystem',
    'Insulation Code': 'insulationCode',
    'Document No.': 'documentNo',
    'Type': 'type'
};

// Permit and Container are their own tag types, not aliases of Scaffold (AVA-493 WS-5 / AC-13).
// Their titles start identical to Scaffold's and are written out in full rather than spread from
// it, so that a later per-type change cannot reach across types.
export const permitExcelTitlesMap = {
    'Tag No.*': 'name',
    'Plant': 'plant',
    'Unit': 'unit',
    'Functional Location': 'funcLocation',
    'Area': 'area',
    'System': 'system',
    'Sub System': 'subSystem',
    'Insulation Code': 'insulationCode',
    'Document No.': 'documentNo',
    'Type': 'type'
};

export const containerExcelTitlesMap = {
    'Tag No.*': 'name',
    'Plant': 'plant',
    'Unit': 'unit',
    'Functional Location': 'funcLocation',
    'Area': 'area',
    'System': 'system',
    'Sub System': 'subSystem',
    'Insulation Code': 'insulationCode',
    'Document No.': 'documentNo',
    'Type': 'type'
};
